import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';

import { PrimaryTradeLicenseRequirement, Site } from '../classes/models';
import { PrimaryTrade } from '../classes/models/primary-trade';
import { TradeCategory } from '../classes/models/trade-category';
import { TradeSpecialtyCategory } from '../classes/models/trade-specialty-category';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class TradeService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  getTrades(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<PrimaryTrade[]>;
        } = {
          Vendor: () =>
            this.http.get<PrimaryTrade[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<PrimaryTrade[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<PrimaryTrade[]>(`/api/v2/admin/trade/many`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTradesCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/trade/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveTrade(PrimaryTrade: PrimaryTrade, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<PrimaryTrade>;
        } = {
          Admin: () =>
            this.http.post<PrimaryTrade>(
              `/api/v2/admin/trade/one`,
              PrimaryTrade,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteTrade(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Admin: () =>
            this.http.delete<number>(`/api/v2/admin/trade`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTradeRequirement(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<PrimaryTradeLicenseRequirement>;
        } = {
          Vendor: () =>
            this.http.get<PrimaryTradeLicenseRequirement>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/requirement/one`,
              { params: query }
            ),
          Client: () =>
            this.http.get<PrimaryTradeLicenseRequirement>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/requirement/one`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<PrimaryTradeLicenseRequirement>(
              `/api/v2/admin/trade/requirement/one`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTradeRequirements(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<PrimaryTradeLicenseRequirement[]>;
        } = {
          Vendor: () =>
            this.http.get<PrimaryTradeLicenseRequirement[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/requirement/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<PrimaryTradeLicenseRequirement[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/requirement/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<PrimaryTradeLicenseRequirement[]>(
              `/api/v2/admin/trade/requirement/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTradeRequirementsCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/requirement/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/requirement/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/trade/requirement/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveTradeRequirement(
    PrimaryTradeLicenseRequirement: PrimaryTradeLicenseRequirement,
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<PrimaryTradeLicenseRequirement>;
        } = {
          Admin: () =>
            this.http.post<PrimaryTradeLicenseRequirement>(
              `/api/v2/admin/trade/requirement/one`,
              PrimaryTradeLicenseRequirement,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveTradeRequirements(
    PrimaryTradeLicenseRequirements: PrimaryTradeLicenseRequirement[],
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<PrimaryTradeLicenseRequirement>;
        } = {
          Admin: () =>
            this.http.post<PrimaryTradeLicenseRequirement>(
              `/api/v2/admin/trade/requirement/many`,
              PrimaryTradeLicenseRequirements,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteTradeRequirement(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Admin: () =>
            this.http.delete<number>(`/api/v2/admin/trade/requirement`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTradeCategories(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<TradeCategory[]>;
        } = {
          Vendor: () =>
            this.http.get<TradeCategory[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/category/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<TradeCategory[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/category/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<TradeCategory[]>(
              `/api/v2/admin/trade/category/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTradeCategoriesCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/category/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/category/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/trade/category/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveTradeCategory(TradeCategory: TradeCategory, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<TradeCategory>;
        } = {
          Admin: () =>
            this.http.post<TradeCategory>(
              `/api/v2/admin/trade/category`,
              TradeCategory,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteTradeCategory(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Admin: () =>
            this.http.delete<number>(`/api/v2/admin/trade/category`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTradeSpecialtyCategories(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<TradeSpecialtyCategory[]>;
        } = {
          Vendor: () =>
            this.http.get<TradeSpecialtyCategory[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/specialty/category/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<TradeSpecialtyCategory[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/specialty/category/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<TradeSpecialtyCategory[]>(
              `/api/v2/admin/trade/specialty/category/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getTradeSpecialtyCategoriesCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/trade/specialty/category/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/trade/specialty/category/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(
              `/api/v2/admin/trade/specialty/category/count`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveTradeSpecialtyCategory(
    TradeSpecialtyCategory: TradeSpecialtyCategory,
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<TradeSpecialtyCategory>;
        } = {
          Admin: () =>
            this.http.post<TradeSpecialtyCategory>(
              `/api/v2/admin/trade/specialty/category`,
              TradeSpecialtyCategory,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteTradeSpecialtyCategory(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Admin: () =>
            this.http.delete<number>(`/api/v2/admin/trade/specialty/category`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
